<template>
  <div id="table-responsive" class="table-responsive">
    <div class="button-container">
      <div class="folder-name">{{ category?.categoryName }}</div>
      <div class="flex-row">
        <div class="toolbar-container">
          <Toolbar>
            <ToolbarLabel :label="toolbarLabel" />
            <ToolbarIconButton
              v-for="(button, b) in buttons"
              :key="`button-${b}`"
              v-bind="button"
            />
          </Toolbar>
          <AddNewRecordCollection
            v-if="isRecordEditor"
            :grid-api="gridApi"
            :number-of-rows="recordCollections?.length"
          />
          <AddNewEntryButton
            v-else
            :category-i-d="categoryID"
            :grid-api="gridApi"
          />
          <div class="saving-message">{{ savingMessage }}</div>
        </div>
        <div class="popover-container">
          <KeyboardShortcuts />
          <FindAndReplace v-if="!isRecordEditor" :grid-api="gridApi" />
        </div>
      </div>
    </div>
    <BulkEditorChangeLayoutMultiple
      ref="changeLayoutRef"
      :selected-items="selectedRows"
      :grid-api="gridApi"
    />
    <ProfileBulkEditorTable
      v-if="!isRecordEditor"
      class="bulk-edit-body"
      @grid-ready="handleGridReady"
      @selected-rows-changed="handleSelectedRowsChanged"
      @custom-menu-action="executeCustomMenuAction"
    />
    <RecordCollectionBulkEditorTable
      v-else
      class="bulk-edit-body"
      @grid-ready="handleGridReady"
      @selected-rows-changed="handleSelectedRowsChanged"
    />
    <Button
      variant="primary"
      elevation="raised"
      data-testid="back-button"
      tooltip="Back"
      tooltip-position="inline-end"
      class="back-button"
      data-dd="Profile Bulk Editor: Back Button"
      @click="goBack"
    >
      <template #left-icon>
        <Icon name="arrow-left" />
      </template>
    </Button>
  </div>
</template>

<script setup>
import { computed, ref, defineAsyncComponent } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import useBackButton from "@/util/useBackButton.js";
import useProfileBulkEditorToolbarActions from "@/components/CMSComponents/Toolbar/useProfileBulkEditorToolbarActions.js";
import useRecordCollectionBulkEditorToolbarActions from "@/components/CMSComponents/Toolbar/useRecordCollectionBulkEditorToolbarActions.js";
import useBulkEditorCustomColumnActions from "@/components/BulkEditorComponents/useBulkEditorCustomColumnActions.js";
import BulkEditorChangeLayoutMultiple from "@/components/BulkEditorComponents/BulkEditorChangeLayoutMultiple.vue";
import FindAndReplace from "@/components/BulkEditorComponents/FindAndReplace.vue";
import AddNewEntryButton from "@/components/BulkEditorComponents/AddNewEntryButton.vue";
import Toolbar from "@/components/CMSComponents/Toolbar/Toolbar.vue";
import ToolbarIconButton from "@/components/CMSComponents/Toolbar/ToolbarIconButton.vue";
import ToolbarLabel from "@/components/CMSComponents/Toolbar/ToolbarLabel.vue";
import Button from "@/v2/primitives/Button/Button.vue";
import AddNewRecordCollection from "@/components/BulkEditorComponents/AddNewRecordCollection.vue";
import Icon from "@/v2/primitives/Icon/Icon.vue";
import KeyboardShortcuts from "@/components/BulkEditorComponents/KeyboardShortcuts.vue";

const ProfileBulkEditorTable = defineAsyncComponent(() =>
  import("@/components/BulkEditorComponents/ProfileBulkEditorTable.vue")
);

const RecordCollectionBulkEditorTable = defineAsyncComponent(() =>
  import(
    "@/components/BulkEditorComponents/RecordCollectionBulkEditorTable.vue"
  )
);

const store = useStore();
const route = useRoute();
const { goBack } = useBackButton();

const categoryID = computed(() => route.params.categoryID);
const category = computed(() =>
  store.getters["api/category"](categoryID.value)
);

const isRecordEditor = computed(() =>
  ["RecordsColumnsLayout", "RecordsRowsLayout"].includes(category.value?.layout)
);
const entryData = computed(() => category.value?.entries);
const recordCollections = computed(() => category.value?.recordColumns);
const isInternalUser = computed(() => store.getters.getIsInternal);

const selectedRows = ref([]);
const numberSelected = computed(() => selectedRows.value.length);
const changeLayoutRef = ref(null);
const gridApi = ref(null);

const { executeCustomMenuAction } =
  useBulkEditorCustomColumnActions(selectedRows);

const handleSelectedRowsChanged = (newSelectedRows) => {
  selectedRows.value = newSelectedRows;
};

const showChangeProfileLayoutModal = () => {
  if (changeLayoutRef.value) changeLayoutRef.value.showModal();
};

const handleGridReady = (api) => {
  gridApi.value = api;
};

const savingMessage = computed(() =>
  store.getters.getIsSaving ? "Saving..." : "Saved"
);

const { isLive, deleteEntries, togglePublish } =
  useProfileBulkEditorToolbarActions(entryData, selectedRows, gridApi);

const { deleteRecordCollections } = useRecordCollectionBulkEditorToolbarActions(
  recordCollections,
  selectedRows
);

const downloadAsCSV = () => {
  const params = {
    columnKeys: [
      "firstName",
      "lastName",
      "graduationDate",
      "eventDate",
      "activity",
      "type",
      "description",
    ],
  };
  gridApi.value.exportDataAsCsv(params);
};

const entryButtons = computed(() => {
  const buttons = [
    {
      icon: isLive.value ? "eye-slash" : "eye",
      label: isLive.value ? "Unpublish" : "Publish",
      handleClick: togglePublish,
      disabled: !numberSelected.value,
      showTooltip: true,
    },
    {
      icon: "trash",
      label: "Delete",
      handleClick() {
        if (!confirm("Are you sure you want to delete this?")) return;
        deleteEntries();
        selectedRows.value = [];
      },
      disabled: !numberSelected.value,
      showTooltip: true,
    },
    {
      icon: "grip",
      label: "Change Profile Layout",
      handleClick: showChangeProfileLayoutModal,
      showTooltip: true,
    },
  ];

  if (isInternalUser.value) {
    buttons.push({
      icon: "download",
      label: "Download as CSV",
      handleClick: downloadAsCSV,
      showTooltip: true,
    });
  }

  return buttons;
});

const recordButtons = computed(() => [
  {
    icon: "trash",
    label: "Delete",
    handleClick: deleteRecordCollections,
    disabled: !numberSelected.value,
    showTooltip: true,
  },
]);

const buttons = computed(() =>
  isRecordEditor.value ? recordButtons.value : entryButtons.value
);

const toolbarLabel = computed(
  () =>
    `${numberSelected.value} ${
      isRecordEditor.value ? "record columns" : "profiles"
    } selected`
);
</script>

<style scoped lang="scss">
@import "@/v2/styles/main";

.table-responsive {
  height: 100%;
  width: 100vw !important;
  overflow-y: scroll; /* Show vertical scrollbar */
  overflow-x: scroll;
  font-size: 11px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.438);
  /* Prevents triggering "Go Back" browser functionality */
  overscroll-behavior-x: none;
  background-color: #f6f9fc;
}
.button-container {
  position: sticky;
  top: 0;
  left: 0;
  height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 15px;
  padding: 0px 15px;
  z-index: 9999;
  background-color: #f6f9fc;
  font-family: "Lato", "Inter";
}
.bulk-edit-body {
  background: white;
}
.toolbar-container {
  display: flex;
  align-items: center;
  gap: 15px;
}
.flex-row {
  display: flex;
  justify-content: space-between;
}
:deep(.embed-link-input) {
  font-size: 10px;
}
.saving-message {
  font-size: 14px;
}
.bulk-edit-body {
  position: relative;
}
.back-button {
  position: absolute;
  bottom: var(--spacing-base);
  left: var(--spacing-base);
  z-index: var(--z-index-button-zones);
}
.add-record-collection {
  position: relative;
}
.folder-name {
  font-size: 20px;
  font-weight: bold;
}
.popover-container {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}
</style>
